import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Divider,
  CircularProgress,
  Alert,
  Button,
  useTheme,
  useMediaQuery,
  Chip,
  Stack,
} from "@mui/material";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import ReceiptIcon from "@mui/icons-material/Receipt";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import LocalAtmIcon from "@mui/icons-material/LocalAtm";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import RefreshIcon from "@mui/icons-material/Refresh";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import * as XLSX from "xlsx";
import { getCompanyProfile } from "../../../../services/Reports/ReportsApi";

const ProfitandLossReport = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [reportData, setReportData] = useState(null);
  const [exporting, setExporting] = useState(false);

  // جلب البيانات عند تحميل المكون
  useEffect(() => {
    fetchReport();
  }, []);

  const fetchReport = async () => {
    try {
      setLoading(true);
      setError("");
      const data = await getCompanyProfile();
      setReportData(data);
    } catch (err) {
      console.error(err);
      setError(err.message || "فشل في تحميل تقرير الأرباح والخسائر");
    } finally {
      setLoading(false);
    }
  };

  // تنسيق الأرقام
  const formatNumber = (num) => {
    return new Intl.NumberFormat('ar-EG', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(num || 0);
  };

  // حساب صافي الربح
  const getNetProfit = () => {
    if (!reportData) return 0;
    return reportData.total_income - reportData.total_outcome;
  };

  // دالة تصدير تقرير الأرباح والخسائر إلى Excel
  const exportProfitToExcel = () => {
    if (!reportData) return;
    try {
      setExporting(true);
      const wb = XLSX.utils.book_new();

      const rows = [
        ["البند", "القيمة"],
        ["الرسالة", reportData.message || ""],
        ["إجمالي المبيعات", reportData.total_sales ?? 0],
        ["تكلفة الأصناف", reportData.total_items_cost ?? 0],
        ["ربح الفواتير", reportData.total_invoice_profit ?? 0],
        ["التوصيل", reportData.total_invoce_delivery ?? 0],
        ["الضرائب", reportData.total_invoce_tax ?? 0],
        ["الخصومات", reportData.total_invoce_discount ?? 0],
        ["الإيداعات", reportData.total_deposit ?? 0],
        ["السحوبات", reportData.total_withdrawal ?? 0],
        ["المصروفات العامة", reportData.total_expenses ?? 0],
        ["إجمالي الإيرادات", reportData.total_income ?? 0],
        ["إجمالي المصروفات", reportData.total_outcome ?? 0],
        ["صافي الربح", getNetProfit() ?? 0]
      ];

      const ws = XLSX.utils.aoa_to_sheet(rows);
      ws['!cols'] = [{ wch: 30 }, { wch: 18 }];
      XLSX.utils.book_append_sheet(wb, ws, "أرباح_وخسائر");

      const fileName = `تقرير_أرباح_وخسائر_${new Date().toISOString().slice(0,10)}.xlsx`;
      XLSX.writeFile(wb, fileName);
    } catch (e) {
      console.error("Export error:", e);
      alert("فشل تصدير الملف.");
    } finally {
      setExporting(false);
    }
  };

  // مكون بطاقة الإحصائية
  const StatCard = ({ title, value, icon, color, trend }) => (
    <Card 
      sx={{ 
        height: '100%',
        transition: 'transform 0.2s, box-shadow 0.2s',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: 4
        }
      }}
    >
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <Box sx={{ flex: 1 }}>
            <Typography 
              variant="body2" 
              color="text.secondary" 
              sx={{ 
                mb: 1,
                fontSize: { xs: '0.85rem', md: '0.95rem' },
                fontWeight: 500
              }}
            >
              {title}
            </Typography>
            <Typography 
              variant="h5" 
              sx={{ 
                fontWeight: 700,
                color: color || 'text.primary',
                fontSize: { xs: '1.25rem', md: '1.5rem' }
              }}
            >
              {formatNumber(value)}
            </Typography>
            <Typography 
              variant="caption" 
              color="text.secondary"
              sx={{ fontSize: { xs: '0.7rem', md: '0.75rem' } }}
            >
              جنية مصري
            </Typography>
          </Box>
          <Box
            sx={{
              backgroundColor: `${color || theme.palette.primary.main}15`,
              borderRadius: 2,
              p: { xs: 1, md: 1.5 },
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            {React.cloneElement(icon, { 
              sx: { 
                fontSize: { xs: 28, md: 36 }, 
                color: color || theme.palette.primary.main 
              } 
            })}
          </Box>
        </Box>
        {trend && (
          <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {trend > 0 ? (
              <TrendingUpIcon sx={{ fontSize: 18, color: 'success.main' }} />
            ) : (
              <TrendingDownIcon sx={{ fontSize: 18, color: 'error.main' }} />
            )}
            <Typography 
              variant="caption" 
              sx={{ 
                color: trend > 0 ? 'success.main' : 'error.main',
                fontWeight: 600
              }}
            >
              {trend > 0 ? '+' : ''}{trend}%
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );

  // مكون صف التفاصيل
  const DetailRow = ({ label, value, highlight = false, isTotal = false }) => (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        py: { xs: 1.5, md: 2 },
        px: { xs: 1, md: 2 },
        backgroundColor: highlight ? 'action.hover' : 'transparent',
        borderRadius: 1,
        ...(isTotal && {
          borderTop: '2px solid',
          borderColor: 'divider',
          mt: 2,
          pt: 2
        })
      }}
    >
      <Typography
        variant="body1"
        sx={{
          fontWeight: isTotal ? 700 : 500,
          fontSize: { xs: '0.9rem', md: '1rem' },
          color: isTotal ? 'primary.main' : 'text.primary'
        }}
      >
        {label}
      </Typography>
      <Typography
        variant="body1"
        sx={{
          fontWeight: isTotal ? 700 : 600,
          fontSize: { xs: '0.95rem', md: '1.1rem' },
          color: isTotal ? 'primary.main' : 'text.primary'
        }}
      >
        {formatNumber(value)} ج.م
      </Typography>
    </Box>
  );

  if (loading) {
    return (
      <Box 
        sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          minHeight: 400,
          flexDirection: 'column',
          gap: 2
        }}
      >
        <CircularProgress size={60} />
        <Typography variant="body1" color="text.secondary">
          جاري تحميل تقرير الأرباح والخسائر...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert 
        severity="error" 
        sx={{ 
          fontSize: { xs: '0.9rem', md: '1rem' },
          mb: 2
        }}
        action={
          <Button color="inherit" size="small" onClick={fetchReport}>
            إعادة المحاولة
          </Button>
        }
      >
        {error}
      </Alert>
    );
  }

  if (!reportData) {
    return (
      <Alert severity="info" sx={{ fontSize: { xs: '0.9rem', md: '1rem' } }}>
        لا توجد بيانات متاحة
      </Alert>
    );
  }

  const netProfit = getNetProfit();
  const profitMargin = reportData.total_sales > 0 
    ? ((netProfit / reportData.total_sales) * 100).toFixed(2)
    : 0;

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
      {/* العنوان */}
      <Box sx={{ mb: { xs: 2, md: 3 }, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography 
            variant="h4" 
            sx={{ 
              fontWeight: 700,
              fontSize: { xs: '1.5rem', md: '2rem' },
              mb: 0.5
            }}
          >
            تقرير الأرباح والخسائر
          </Typography>
          <Typography 
            variant="body2" 
            color="text.secondary"
            sx={{ fontSize: { xs: '0.85rem', md: '0.95rem' } }}
          >
            نظرة شاملة على الأداء المالي للشركة
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchReport}
            disabled={loading}
            sx={{ fontSize: { xs: '0.85rem', md: '0.95rem' } }}
          >
            تحديث البيانات
          </Button>

          <Button
            variant="contained"
            color="success"
            startIcon={<FileDownloadIcon />}
            onClick={exportProfitToExcel}
            disabled={exporting || !reportData}
            sx={{ fontSize: { xs: '0.85rem', md: '0.95rem' } }}
          >
            {exporting ? "جاري التصدير..." : "تصدير إلى Excel"}
          </Button>
        </Box>
      </Box>

      {/* رسالة النجاح */}
      {reportData.message && (
        <Alert 
          severity="success" 
          sx={{ 
            mb: 3,
            fontSize: { xs: '0.85rem', md: '0.95rem' }
          }}
        >
          {reportData.message}
        </Alert>
      )}

      {/* البطاقات الرئيسية */}
      <Grid container spacing={{ xs: 2, md: 3 }} sx={{ mb: { xs: 3, md: 4 } }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="إجمالي المبيعات"
            value={reportData.total_sales}
            icon={<ShoppingCartIcon />}
            color={theme.palette.primary.main}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="إجمالي الإيرادات"
            value={reportData.total_income}
            icon={<TrendingUpIcon />}
            color={theme.palette.success.main}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="إجمالي المصروفات"
            value={reportData.total_outcome}
            icon={<TrendingDownIcon />}
            color={theme.palette.error.main}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="صافي الربح"
            value={netProfit}
            icon={<MonetizationOnIcon />}
            color={netProfit >= 0 ? theme.palette.success.main : theme.palette.error.main}
          />
        </Grid>
      </Grid>

      {/* التفاصيل */}
      <Grid container spacing={{ xs: 2, md: 3 }}>
        {/* قسم الإيرادات */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: { xs: 2, md: 3 }, height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
              <TrendingUpIcon sx={{ color: 'success.main', fontSize: { xs: 24, md: 28 } }} />
              <Typography 
                variant="h6" 
                sx={{ 
                  fontWeight: 700,
                  fontSize: { xs: '1.1rem', md: '1.25rem' }
                }}
              >
                الإيرادات
              </Typography>
            </Box>

            <DetailRow label="إجمالي المبيعات" value={reportData.total_sales} />
            <DetailRow label="ربح الفواتير" value={reportData.total_invoice_profit} />
            <DetailRow label="الإيداعات" value={reportData.total_deposit} />
            <DetailRow label="التوصيل" value={reportData.total_invoce_delivery} />
            <DetailRow label="الضرائب المحصلة" value={reportData.total_invoce_tax} />
            
            <Divider sx={{ my: 2 }} />
            <DetailRow 
              label="إجمالي الإيرادات" 
              value={reportData.total_income} 
              isTotal 
            />
          </Paper>
        </Grid>

        {/* قسم المصروفات */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: { xs: 2, md: 3 }, height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
              <TrendingDownIcon sx={{ color: 'error.main', fontSize: { xs: 24, md: 28 } }} />
              <Typography 
                variant="h6" 
                sx={{ 
                  fontWeight: 700,
                  fontSize: { xs: '1.1rem', md: '1.25rem' }
                }}
              >
                المصروفات
              </Typography>
            </Box>

            <DetailRow label="تكلفة الأصناف" value={reportData.total_items_cost} />
            <DetailRow label="المصروفات العامة" value={reportData.total_expenses} />
            <DetailRow label="السحوبات" value={reportData.total_withdrawal} />
            <DetailRow label="الخصومات الممنوحة" value={reportData.total_invoce_discount} />
            
            <Divider sx={{ my: 2 }} />
            <DetailRow 
              label="إجمالي المصروفات" 
              value={reportData.total_outcome} 
              isTotal 
            />
          </Paper>
        </Grid>

        {/* الملخص النهائي */}
        <Grid item xs={12}>
          <Paper 
            sx={{ 
              p: { xs: 2, md: 4 },
              background: netProfit >= 0 
                ? `linear-gradient(135deg, ${theme.palette.success.light}15 0%, ${theme.palette.success.main}05 100%)`
                : `linear-gradient(135deg, ${theme.palette.error.light}15 0%, ${theme.palette.error.main}05 100%)`,
              border: '2px solid',
              borderColor: netProfit >= 0 ? 'success.main' : 'error.main'
            }}
          >
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} md={8}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <Box
                    sx={{
                      backgroundColor: netProfit >= 0 ? 'success.main' : 'error.main',
                      borderRadius: 2,
                      p: { xs: 1.5, md: 2 },
                      display: 'flex'
                    }}
                  >
                    <AttachMoneyIcon sx={{ fontSize: { xs: 32, md: 40 }, color: 'white' }} />
                  </Box>
                  <Box>
                    <Typography 
                      variant="h5" 
                      sx={{ 
                        fontWeight: 700,
                        fontSize: { xs: '1.25rem', md: '1.75rem' },
                        color: netProfit >= 0 ? 'success.main' : 'error.main'
                      }}
                    >
                      {netProfit >= 0 ? 'صافي الربح' : 'صافي الخسارة'}
                    </Typography>
                    <Typography 
                      variant="body2" 
                      color="text.secondary"
                      sx={{ fontSize: { xs: '0.85rem', md: '0.95rem' } }}
                    >
                      نتيجة النشاط التشغيلي
                    </Typography>
                  </Box>
                </Box>

                <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap', gap: 1 }}>
                  <Chip 
                    label={`هامش الربح: ${profitMargin}%`}
                    color={netProfit >= 0 ? 'success' : 'error'}
                    sx={{ fontSize: { xs: '0.8rem', md: '0.85rem' } }}
                  />
                  <Chip 
                    label={`معدل النمو: ${formatNumber(netProfit / reportData.total_sales * 100)}%`}
                    variant="outlined"
                    sx={{ fontSize: { xs: '0.8rem', md: '0.85rem' } }}
                  />
                </Stack>
              </Grid>

              <Grid item xs={12} md={4}>
                <Box sx={{ textAlign: { xs: 'left', md: 'right' } }}>
                  <Typography 
                    variant="h3" 
                    sx={{ 
                      fontWeight: 800,
                      fontSize: { xs: '2rem', md: '3rem' },
                      color: netProfit >= 0 ? 'success.main' : 'error.main',
                      mb: 1
                    }}
                  >
                    {formatNumber(Math.abs(netProfit))}
                  </Typography>
                  <Typography 
                    variant="h6" 
                    color="text.secondary"
                    sx={{ fontSize: { xs: '0.95rem', md: '1.1rem' } }}
                  >
                    جنية مصري
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ProfitandLossReport;
